import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * KalkiPurpleSheet — the "KALKI Intelligence" universal premium bottom sheet.
 *
 *  • Dark Graphite Purple foundation (#160D2B → #24104A).
 *  • Atmospheric purple light; no loud gradients.
 *  • Thin luminous top edge + small centered drag handle.
 *  • Desktop: compact floating bottom panel (max 540px).
 *  • Mobile: true bottom sheet with safe-area bottom padding.
 *  • Scoped `.kalki-dark-sheet` class forces inner tile text/borders to light
 *    tones without touching every tile component individually.
 */
export default function KalkiPurpleSheet({
  open,
  onClose,
  icon,
  eyebrow = "KALKI INTELLIGENCE",
  title,
  subtitle,
  children,
  heightClass = "max-h-[85dvh] md:max-h-[80dvh]",
  testid,
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[70]"
            style={{ background: "rgba(10,6,24,0.55)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid={testid ? `${testid}-overlay` : undefined}
          />
          <div
            className="fixed z-[80] inset-x-0 bottom-0 md:inset-x-auto md:bottom-6 md:left-1/2 pointer-events-none"
            style={{ paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)" }}
          >
          <motion.div
            data-testid={testid}
            className={`kalki-dark-sheet rounded-t-[30px] md:rounded-[32px] overflow-hidden pointer-events-auto mx-auto md:mx-0 ${heightClass}`}
            initial={{ y: "100%", opacity: 0.3 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0.2 }}
            transition={{ type: "spring", damping: 30, stiffness: 240, mass: 0.9 }}
            style={{
              background:
                "radial-gradient(120% 90% at 20% 0%, rgba(93,0,255,0.22) 0%, rgba(93,0,255,0) 55%)," +
                "radial-gradient(100% 80% at 80% 100%, rgba(255,46,154,0.14) 0%, rgba(255,46,154,0) 60%)," +
                "linear-gradient(180deg, #1C1240 0%, #160D2B 55%, #110922 100%)",
              borderTop: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 -40px 80px -30px rgba(93,0,255,0.35), 0 20px 80px rgba(0,0,0,0.5)",
              paddingBottom: "env(safe-area-inset-bottom)",
              // Desktop: offset back by half the width to center under md:left-1/2.
              width: "min(640px, calc(100vw - 48px))",
              marginLeft: typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches ? "-320px" : "0",
              position: "relative",
            }}
          >
            {/* Luminous top edge */}
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-[1px]"
              style={{ background: "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.4) 45%, rgba(255,46,154,0.3) 55%, transparent 90%)" }}
            />
            {/* Drag handle */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-[3px] rounded-full"
              style={{ background: "rgba(255,255,255,0.22)" }}
            />
            {/* Close */}
            <button
              onClick={onClose}
              data-testid="sheet-close"
              aria-label="Close"
              type="button"
              className="absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center transition-colors z-[10]"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "rgba(255,255,255,0.75)",
                zIndex: 10,
              }}
            >
              <X className="w-4 h-4" />
            </button>
            {/* Content */}
            <div className="relative px-6 pt-8 pb-7 md:px-9 md:pt-9 md:pb-9 overflow-y-auto kalki-scroll" style={{ maxHeight: "inherit" }}>
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                {icon && (
                  <span className="text-[16px]" style={{ color: "#CFA8FF", textShadow: "0 0 8px rgba(93,0,255,0.6)" }}>
                    {icon}
                  </span>
                )}
                <span className="text-[10px] font-semibold tracking-[0.34em] uppercase" style={{ color: "rgba(207,168,255,0.72)" }}>
                  {eyebrow}
                </span>
              </div>
              {title && (
                <h2 className="font-serif font-light leading-[1.05] mb-1" style={{ fontSize: "clamp(28px, 3.5vmin, 40px)", color: "#F6F1FF" }}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-1 mb-6 text-[12.5px] tracking-[0.18em] uppercase" style={{ color: "rgba(230,220,255,0.55)" }}>
                  {subtitle}
                </p>
              )}
              <div className="mt-2" style={{ color: "#EDE7FA" }}>
                {children}
              </div>
            </div>

            {/* Scoped dark-theme overrides for existing tiles — reads original text/border tokens
                and remaps them to purple-sheet-friendly tones without rewriting the tiles. */}
            <style>{`
              .kalki-dark-sheet { color: #EDE7FA; }
              .kalki-dark-sheet .text-\\[\\#1C1C1C\\],
              .kalki-dark-sheet .text-\\[\\#1C1C1C\\]\\/80,
              .kalki-dark-sheet .text-slate-900,
              .kalki-dark-sheet h1, .kalki-dark-sheet h2, .kalki-dark-sheet h3, .kalki-dark-sheet h4 { color: #F6F1FF !important; }
              .kalki-dark-sheet .text-\\[\\#676773\\],
              .kalki-dark-sheet .text-slate-500,
              .kalki-dark-sheet .text-slate-600,
              .kalki-dark-sheet .text-slate-700 { color: rgba(230,220,255,0.65) !important; }
              .kalki-dark-sheet .border-black\\/10,
              .kalki-dark-sheet .border-black\\/5 { border-color: rgba(255,255,255,0.12) !important; }
              .kalki-dark-sheet .bg-white\\/70,
              .kalki-dark-sheet .bg-white\\/75,
              .kalki-dark-sheet .bg-white\\/80,
              .kalki-dark-sheet .k-glass { background: rgba(255,255,255,0.06) !important; border-color: rgba(255,255,255,0.12) !important; color: #F6F1FF !important; }
              .kalki-dark-sheet button:not([data-brand]) { color: inherit; }
              .kalki-dark-sheet .kalki-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 999px; }
            `}</style>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
